import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { Timestamp } from 'firebase/firestore';

// Mayor types (matching municipality app structure)
export interface AuditInfo {
  name: string;
  surname: string;
}

export interface Mayor {
  id: string;
  name: string;
  surname: string;
  email?: string;
  profileImage?: string;
  welcomeMessage: string;
  translations?: Record<string, { name?: string; surname?: string; welcomeMessage?: string }>;
  municipalityId: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: AuditInfo;
  updatedBy?: AuditInfo;
}

// Helper to convert Firestore Timestamp to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
};

// Convert Firestore data to Mayor
const convertFirestoreData = (data: unknown, id: string, municipalityId: string): Mayor => {
  const docData = data as Record<string, unknown>;
  
  const createdByData = docData.createdBy as Record<string, unknown> | undefined;
  const createdBy: AuditInfo = createdByData ? {
    name: (createdByData.name as string) || '',
    surname: (createdByData.surname as string) || '',
  } : {
    name: '',
    surname: '',
  };

  const updatedByData = docData.updatedBy as Record<string, unknown> | undefined;
  const updatedBy: AuditInfo | undefined = updatedByData ? {
    name: (updatedByData.name as string) || '',
    surname: (updatedByData.surname as string) || '',
  } : undefined;

  return {
    id,
    name: (docData.name as string) || '',
    surname: (docData.surname as string) || '',
    email: docData.email as string | undefined,
    profileImage: docData.profileImage as string | undefined,
    welcomeMessage: (docData.welcomeMessage as string) || '',
    translations: docData.translations as Record<string, { name?: string; surname?: string; welcomeMessage?: string }> | undefined,
    municipalityId,
    createdAt: toDate(docData.createdAt),
    updatedAt: toDate(docData.updatedAt),
    createdBy,
    updatedBy,
  };
};

export const useMunicipalityMayor = (municipalityId: string | null) => {
  const [data, setData] = useState<Mayor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setLoading(false);
      setData(null);
      return;
    }

    const fetchMayor = async () => {
      try {
        setLoading(true);
        setError(null);

        const mayorDoc = doc(db, 'municipalities', municipalityId, 'mayor', 'mayor');
        const snapshot = await getDoc(mayorDoc);

        if (snapshot.exists()) {
          const mayor = convertFirestoreData(snapshot.data(), snapshot.id, municipalityId);
          setData(mayor);
        } else {
          setData(null);
        }
      } catch (err) {
        console.error('Error fetching municipality mayor:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch municipality mayor');
      } finally {
        setLoading(false);
      }
    };

    fetchMayor();
  }, [municipalityId]);

  return { data, loading, error };
};
